/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, Fragment } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Avatar } from "antd";
import {UserOutlined} from "@ant-design/icons";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
// Redux-hooks:
import { useAppSelector } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getElementState, getTheme, getLng, getUser } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type Tavatar = {
  shape?:"circle" | "square",
  size?:number | "large" | "small" | "default" | {[key:string]:number | "large" | "small" | "default"}
  gap?:number,
  maxCount?:number,
  maxPopoverTrigger?:"click" | "hover" | "focus",
  draggable?:boolean
}

const { rz } = window;

const RZavatar:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZavatar.displayName = "Avatar";
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  // const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const elState = useAppSelector(getElementState(rootAttributes.key, fullPath)); // value, visible, disabled, loading, userStyle, userClass
  const {icon, iconColor, iconClass} = elState.attributes;
  // Добавлено для Avatar.Group и динамических демо: настройки могут прийти не только из XML,
  // но и из Redux state через newState.attributes. Поэтому ниже используем объединенные attributes.
  const mergedAttributes = useMemo(() => ({...attributes, ...elState.attributes}), [attributes, elState.attributes]);
  // const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const avaType = useMemo<Tavatar>(() => {
    // Изменено: раньше shape по умолчанию всегда был "circle" и дочерний Avatar перебивал shape родительской группы.
    // Теперь shape задается только явно, а одиночному Avatar форму по умолчанию дает Ant Design.
    const typ:Tavatar = {draggable: false};
    (mergedAttributes.type || type || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["circle", "square"].indexOf(tt)) { typ.shape = tt }
      else if (~["large", "small"].indexOf(tt)) { typ.size = tt }
      else if (~["click", "hover", "focus"].indexOf(tt)) { typ.maxPopoverTrigger = tt }
      else if (tt.startsWith("gap-")) { typ.gap = Number(tt.replace("gap-", "")) }
      // Исправлено: после toLowerCase() токен maxCount-2 превращается в maxcount-2.
      else if (tt.startsWith("maxcount-")) { typ.maxCount = Number(tt.replace("maxcount-", "")) }
      else if (tt === "draggable") { typ.draggable = true }
    });

    if (mergedAttributes?.size) { typ.size = mergedAttributes.size }
    if (mergedAttributes?.shape) { typ.shape = mergedAttributes.shape }
    if (mergedAttributes?.gap) { typ.gap = Number(mergedAttributes.gap) }
    if (mergedAttributes?.maxCount) { typ.maxCount = Number(mergedAttributes.maxCount) }
    if (mergedAttributes?.maxPopoverTrigger) { typ.maxPopoverTrigger = mergedAttributes.maxPopoverTrigger as Tavatar["maxPopoverTrigger"] }
    if (mergedAttributes?.draggable) { typ.draggable = (mergedAttributes.draggable === true || mergedAttributes.draggable === "true") }
    return typ;
  }, [mergedAttributes, type]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle, verticalAlign: "middle"};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState.userStyle, elState?.visible, permissions]);

  const ElIcon = useMemo(() => {
    if (icon) { return (<AppIcons key={key} icopath={icon} attrs={{className: iconClass, color: iconColor}} />) }
    return (<UserOutlined key={key + "-icon"} className={iconClass} />);
  }, [icon, iconClass, iconColor, key]);

  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng, rlng]);
  const alt = useMemo<string | undefined>(() => (elState.attributes["alt_" + lng] || elState.attributes.alt || elState.attributes["alt_" + rlng]), [elState.attributes, lng, rlng]);
  // Изменено: раньше без value/src получался URL вида "<avatar-url>undefined".
  // Теперь src берется из state или attributes и не передается, если значения нет.
  const src = useMemo<string | undefined>(() => {
    const rawSrc = elState.value || mergedAttributes.src || mergedAttributes.value;
    return rawSrc ? (fnc.config.links.avatar + rawSrc) : undefined;
  }, [elState.value, mergedAttributes.src, mergedAttributes.value]);

  // Изменено: раньше JSX для Avatar.Group создавался, но не возвращался, поэтому дочерние avatar игнорировались.
  // Теперь вложенные <avatar> реально рендерятся как Ant Avatar.Group.
  // Добавлено: фильтруем текстовые whitespace-узлы из XML, потому что Ant Avatar.Group клонирует children как React elements.
  const avatarChildren = React.Children.toArray(children).filter(React.isValidElement);

  if (avatarChildren.length > 0) {
    return (
      <Avatar.Group
        key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
        shape={avaType.shape}
        size={avaType.size}
        maxCount={avaType.maxCount}
        maxPopoverTrigger={avaType.maxPopoverTrigger}
      >
        {avatarChildren}
      </Avatar.Group>
    );
  }

  return (
    <Avatar
      key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
      shape={avaType.shape}
      size={avaType.size}
      gap={avaType.gap}
      alt={alt}
      icon={ElIcon}
      src={src}
      draggable={avaType.draggable}
    />
  );
}

export default RZavatar;
/* eslint-enable @typescript-eslint/no-unused-vars */
